import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

export type OperationType =
  | 'AUTH_ATTEMPT'
  | 'AUTH_SUCCESS'
  | 'AUTH_FAILURE'
  | 'DATA_ACCESS'
  | 'DATA_MODIFICATION'
  | 'FILE_UPLOAD'
  | 'FILE_DELETE'
  | 'BACKUP_CREATE'
  | 'BACKUP_RESTORE'
  | 'PERMISSION_CHANGE'
  | 'SETTINGS_CHANGE'
  | 'SECURITY_VIOLATION';

export type SeverityLevel = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

export interface SecurityEvent {
  operation: string;
  operationType: OperationType;
  userId?: string;
  orgId?: string;
  success: boolean;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
  severity: SeverityLevel;
}

/**
 * Log a security event to the audit log
 */
export async function logSecurityEvent(event: SecurityEvent): Promise<void> {
  try {
    // Log to console in development
    if (import.meta.env.DEV) {
      logger.info('[SECURITY AUDIT]', event);
    }

    // Insert into security_audit_logs table
    const { error } = await supabase.from('security_audit_logs').insert({
      operation: event.operation,
      operation_type: event.operationType,
      clerk_user_id: event.userId,
      clerk_org_id: event.orgId,
      success: event.success,
      error_message: event.errorMessage,
      metadata: event.metadata,
      severity: event.severity,
    });

    if (error) {
      // Don't throw - logging failures shouldn't break the app
      logger.error('Failed to log security event:', error);
    }
  } catch (error) {
    logger.error('Security event logging error:', error);
  }
}

/**
 * Log successful authentication
 */
export async function logAuthSuccess(
  userId: string,
  orgId: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await logSecurityEvent({
    operation: 'User authenticated successfully',
    operationType: 'AUTH_SUCCESS',
    userId,
    orgId,
    success: true,
    metadata,
    severity: 'INFO',
  });
}

/**
 * Log failed authentication
 */
export async function logAuthFailure(
  userId?: string,
  errorMessage?: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await logSecurityEvent({
    operation: 'Authentication failed',
    operationType: 'AUTH_FAILURE',
    userId,
    success: false,
    errorMessage,
    metadata,
    severity: 'WARNING',
  });
}

/**
 * Log rate limit violation
 */
export async function logRateLimitViolation(
  identifier: string,
  endpoint: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await logSecurityEvent({
    operation: `Rate limit exceeded: ${endpoint}`,
    operationType: 'SECURITY_VIOLATION',
    userId: identifier,
    success: false,
    errorMessage: 'Rate limit exceeded',
    metadata: { endpoint, ...metadata },
    severity: 'WARNING',
  });
}

/**
 * Log sensitive data operation
 */
export async function logDataOperation(
  operation: string,
  userId: string,
  orgId: string,
  success: boolean,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await logSecurityEvent({
    operation,
    operationType: 'DATA_MODIFICATION',
    userId,
    orgId,
    success,
    metadata,
    severity: success ? 'INFO' : 'WARNING',
  });
}

/**
 * Log file upload
 */
export async function logFileUpload(
  fileName: string,
  userId: string,
  orgId: string,
  success: boolean,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await logSecurityEvent({
    operation: `File uploaded: ${fileName}`,
    operationType: 'FILE_UPLOAD',
    userId,
    orgId,
    success,
    metadata: { fileName, ...metadata },
    severity: success ? 'INFO' : 'ERROR',
  });
}

/**
 * Log security violation
 */
export async function logSecurityViolation(
  violation: string,
  userId?: string,
  orgId?: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await logSecurityEvent({
    operation: violation,
    operationType: 'SECURITY_VIOLATION',
    userId,
    orgId,
    success: false,
    errorMessage: violation,
    metadata,
    severity: 'CRITICAL',
  });
}

/**
 * Fetch recent security events from the database
 */
export async function getRecentSecurityEvents(limit: number = 100) {
  const { data, error } = await supabase
    .from('security_audit_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    logger.error('Failed to fetch security events:', error);
    return [];
  }

  return data;
}

/**
 * Fetch failed authentication attempts
 */
export async function getFailedAuthAttempts(hoursBack: number = 24) {
  const { data, error } = await supabase
    .from('security_audit_logs')
    .select('*')
    .eq('operation_type', 'AUTH_FAILURE')
    .gte('created_at', new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Failed to fetch failed auth attempts:', error);
    return [];
  }

  return data;
}

/**
 * Fetch security violations
 */
export async function getSecurityViolations(hoursBack: number = 24) {
  const { data, error } = await supabase
    .from('security_audit_logs')
    .select('*')
    .eq('operation_type', 'SECURITY_VIOLATION')
    .gte('created_at', new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Failed to fetch security violations:', error);
    return [];
  }

  return data;
}
